import React from "react";
import { Card } from "./ui/card";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { CheckCircle2, Shuffle, Layers, RotateCw, Scale } from "lucide-react";
import { PATHI_BY_ID, PREACHER_BY_ID, withHonorific } from "../data/rssbData";

const Ring = ({ percent }) => {
  const r = 26;
  const c = 2 * Math.PI * r;
  const off = c - (percent / 100) * c;
  return (
    <div className="relative w-16 h-16 shrink-0">
      <svg className="w-16 h-16 -rotate-90" viewBox="0 0 64 64">
        <circle cx="32" cy="32" r={r} fill="none" stroke="#cdb98f" strokeWidth="6" />
        <circle
          cx="32"
          cy="32"
          r={r}
          fill="none"
          stroke="#6e1b1b"
          strokeWidth="6"
          strokeLinecap="round"
          strokeDasharray={c}
          strokeDashoffset={off}
        />
      </svg>
      <span className="absolute inset-0 grid place-items-center text-[#3a1212] font-bold text-sm">
        {percent}%
      </span>
    </div>
  );
};

const Metric = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-1.5 text-xs text-[#7a4a3a]">
    <Icon className="w-3.5 h-3.5 text-[#6e1b1b]" />
    <span>{label}</span>
    <span className="ml-auto font-semibold text-[#3a1212]">{value}%</span>
  </div>
);

export const VariantCards = ({ variants, selectedId, onSelect, onReshuffle }) => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
    {variants.map((v, i) => {
      const active = v.id === selectedId;
      return (
        <Card
          key={v.id}
          data-testid={`variant-card-${i}`}
          onClick={() => onSelect(v.id)}
          className={`card-3d animate-fade-up p-4 cursor-pointer rounded-xl transition-all duration-300 bg-[#fffdf7] ${
            active
              ? "border-2 border-[#5e1414] ring-2 ring-[#6e1b1b]/40 shadow-[0_0_20px_rgba(94,20,20,0.25)]"
              : "border border-[#e3d6b6] hover:border-[#6e1b1b]/60"
          }`}
          style={{ animationDelay: `${i * 60}ms` }}
        >
          <div className="flex items-center gap-3">
            <Ring percent={v.score.percent} />
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h4 className="font-serif-dev font-bold text-[#3a1212]">विकल्प {i + 1}</h4>
                {active && (
                  <Badge className="bg-[#5e1414] text-[#f3ead6] hover:bg-[#5e1414] gap-1 shadow-[0_2px_6px_rgba(94,20,20,0.4)]">
                    <CheckCircle2 className="w-3 h-3" /> चयनित
                  </Badge>
                )}
              </div>
              <p className="text-xs text-[#7a4a3a]">गुणवत्ता स्कोर / Quality Score</p>
            </div>
          </div>
          <div className="mt-3 space-y-1.5">
            <Metric icon={Layers} label="कवरेज / Coverage" value={v.score.coverage} />
            <Metric icon={RotateCw} label="रोटेशन / Rotation" value={v.score.rotation} />
            <Metric icon={Scale} label="संतुलन / Load" value={v.score.balance} />
          </div>
          <div data-testid={`variant-idle-${i}`} className={`mt-2 text-[11px] font-dev ${v.idle?.length ? "text-[#b91c1c]" : "text-[#2f6b3a]"}`}>
            {v.idle?.length
              ? `बिना सेवा (${v.idle.length}): ${v.idle.map((id) => withHonorific(PATHI_BY_ID[id] || PREACHER_BY_ID[id])).join(", ")}`
              : "✓ सभी सेवादारों को कम से कम 1 सेवा"}
          </div>
          <div className="mt-3 flex gap-2">
            <Button
              size="sm"
              variant="secondary"
              data-testid={`variant-select-${i}`}
              onClick={(e) => {
                e.stopPropagation();
                onSelect(v.id);
              }}
              className="flex-1 bg-[#e3d6b6] text-[#3a1212] hover:bg-[#cdb98f]"
            >
              चुनें
            </Button>
            <Button
              size="sm"
              variant="outline"
              data-testid={`variant-reshuffle-${i}`}
              onClick={(e) => {
                e.stopPropagation();
                onReshuffle(v.id);
              }}
              className="border-[#6e1b1b]/50 text-[#6e1b1b] hover:bg-[#6e1b1b]/10 gap-1"
            >
              <Shuffle className="w-3.5 h-3.5" /> री-शफल
            </Button>
          </div>
        </Card>
      );
    })}
  </div>
);
